import type React from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { Waypoint } from 'src/app/shared/components/scroll';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';

import { PERSONALIZED_TASK_STATUSES } from '../../constants';
import { useTaskCompletion } from '../../hooks/useTaskCompletion';
import type { DynamicTaskShape } from '../../types';

import { TaskHeader } from './components/TaskHeader';
import { ExpandedContent, getPrimaryActionText, renderStartBadge } from './helpers';

type DynamicTaskProps = Readonly<{
	task: DynamicTaskShape;
	isExpanded: boolean;
	onToggleExpand: () => void;
}>;

/**
 * Renders a single dynamic task with header, optional details, and action button.
 * Supports mobile-responsive layout with edge-to-edge hover highlighting.
 */
export const DynamicTask: React.FC<DynamicTaskProps> = ({ task, isExpanded, onToggleExpand }) => {
	const { translate } = useI18n();
	const { title, description, reasoning, isFirstActive, featureKey, onCtaClick, onCheckboxChange, status } = task;
	const isCompleted = status === PERSONALIZED_TASK_STATUSES.COMPLETED;
	const primaryActionText = getPrimaryActionText(featureKey, translate);
	const { isChecked, handleButtonClick, handleCheckboxChange } = useTaskCompletion({
		isCompleted,
		onCtaClick,
		onCheckboxChange,
	});

	const track = useTracking();
	const trackOnce = useTrackOnce();

	const trackingTask = featureKey ?? 'mark_as_done';
	const baseTrackingData = {
		task: trackingTask,
		task_name: title,
		version: 'new_ai_03_2026' as const,
	};

	const handleToggleExpand = () => {
		void track(isExpanded ? 'petition_manage_tasks_close' : 'petition_manage_tasks_open', baseTrackingData);
		onToggleExpand();
	};

	const isInteractiveTarget = (target: EventTarget) =>
		(target as HTMLElement).closest?.('button, input, label, a, [role="checkbox"]') != null;

	const containerRef = useRef<HTMLDivElement>(null);
	const handleToggleExpandRef = useRef(handleToggleExpand);
	useLayoutEffect(() => {
		handleToggleExpandRef.current = handleToggleExpand;
	});

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return undefined;
		const onClick = (e: MouseEvent) => {
			if (!isInteractiveTarget(e.target as EventTarget)) handleToggleExpandRef.current();
		};
		el.addEventListener('click', onClick);
		return () => el.removeEventListener('click', onClick);
	}, []);

	return (
		<div
			ref={containerRef}
			className="border-stroke-weaker group hover:bg-fill-inverse-weak -mx-4 border-b border-solid px-4 py-5 transition-colors duration-100 first:border-t md:-mx-12 md:px-12"
			data-qa="dynamic-task"
		>
			<Waypoint onEnter={async () => trackOnce('petition_manage_tasks_view', baseTrackingData)} />
			<div className="flex flex-col gap-3">
				{isFirstActive && renderStartBadge(translate)}
				<TaskHeader
					title={title}
					isFirstActive={isFirstActive}
					isChecked={isChecked}
					hasCta={!!onCtaClick}
					isCompleted={isCompleted}
					isExpanded={isExpanded}
					primaryActionText={primaryActionText}
					onToggleExpand={handleToggleExpand}
					onCheckboxChange={handleCheckboxChange}
					onButtonClick={handleButtonClick}
				/>
				{isExpanded && (
					<ExpandedContent
						description={description}
						reasoning={reasoning}
						isFirstActive={isFirstActive}
						isCompleted={isCompleted}
						primaryActionText={primaryActionText}
						hasCta={!!onCtaClick}
						onButtonClick={handleButtonClick}
					/>
				)}
			</div>
		</div>
	);
};
